import React, { useEffect, useState, useCallback } from "react";
import AdminNav from "../components/Admin/AdminNav";
import { API_URL } from "../config";
import { authHeader, canManageProducts } from "../auth";
import styles from "./discounts.module.css";

async function readError(response, fallback) {
  const text = await response.text();
  try {
    return JSON.parse(text).message || fallback;
  } catch {
    return text || fallback;
  }
}

// One page for the catalogue taxonomy:
// - Types (categories): what a product IS (Tees, Bags, …)
// - Collections: the drop/line a product belongs to (Military Coup Deluxe, …)
const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [typeName, setTypeName] = useState("");
  const [collectionName, setCollectionName] = useState("");
  const [saving, setSaving] = useState(false);
  const canManage = canManageProducts();

  const fetchAll = useCallback(async () => {
    // Load each list independently so one failing endpoint doesn't blank the other
    const problems = [];
    try {
      const res = await fetch(`${API_URL}/categories`);
      if (!res.ok) throw new Error();
      setCategories(await res.json());
    } catch {
      problems.push("types");
    }
    try {
      const res = await fetch(`${API_URL}/collections`);
      if (!res.ok) throw new Error();
      setCollections(await res.json());
    } catch {
      problems.push("collections");
    }
    setError(problems.length ? `Could not load: ${problems.join(", ")}.` : "");
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const flash = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 4000);
  };

  // resource: "categories" | "collections"
  const create = async (resource, name, extra = {}) => {
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${API_URL}/${resource}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ name: name.trim(), ...extra }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not create it."));
      }
      flash(`${name.trim()} added.`);
      fetchAll();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const rename = async (resource, item, extra = {}) => {
    const next = window.prompt(`Rename "${item.name}" to:`, item.name);
    if (!next || next.trim() === item.name) return;
    setError("");
    try {
      const response = await fetch(`${API_URL}/${resource}/${item._id || item.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ name: next.trim(), ...extra }),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not rename it."));
      }
      flash(`Renamed to ${next.trim()} — live across the store immediately.`);
      fetchAll();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (resource, item, label) => {
    if (!window.confirm(`Delete the ${label} "${item.name}"?`)) return;
    setError("");
    try {
      const response = await fetch(`${API_URL}/${resource}/${item._id || item.id}`, {
        method: "DELETE",
        headers: authHeader(),
      });
      if (!response.ok) {
        throw new Error(await readError(response, "Could not delete it."));
      }
      flash(`${item.name} deleted.`);
      fetchAll();
    } catch (err) {
      setError(err.message);
    }
  };

  const renderList = (items, resource, label, renameExtra) => (
    <ul className={styles.list}>
      {items.map((item) => (
        <li key={item._id || item.id} className={styles.row}>
          <div className={styles.rowInfo}>
            <span className={styles.code}>{item.name}</span>
          </div>
          {canManage && (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.actionBtn}
                onClick={() => rename(resource, item, renameExtra ? renameExtra(item) : {})}
              >
                Rename
              </button>
              <button
                type="button"
                className={styles.dangerBtn}
                onClick={() => remove(resource, item, label)}
              >
                Delete
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <div className={styles.page}>
      <AdminNav />
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Backoffice</span>
          <h1 className={styles.title}>Types & Collections</h1>
          <p className={styles.subtitle}>
            Types say what a product is (Tees, Bags…); collections say which
            drop it belongs to. Both are assigned when adding or editing a
            product.
          </p>
        </header>

        {error && <p className={styles.error}>{error}</p>}
        {notice && <p className={styles.success}>{notice}</p>}

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Product Types</h2>
          {loading ? (
            <p className={styles.loading}>Loading…</p>
          ) : categories.length === 0 ? (
            <p className={styles.loading}>No types yet.</p>
          ) : (
            renderList(categories, "categories", "type", (item) => ({
              icon: item.icon,
              color: item.color,
            }))
          )}
          {canManage && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (await create("categories", typeName, { icon: "tag", color: "#141210" })) {
                  setTypeName("");
                }
              }}
            >
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="c-name">New type</label>
                  <input
                    id="c-name"
                    className={styles.input}
                    required
                    placeholder="Headwear"
                    value={typeName}
                    onChange={(e) => setTypeName(e.target.value)}
                  />
                </div>
              </div>
              <button className={styles.submit} type="submit" disabled={saving}>
                Add type
              </button>
            </form>
          )}
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>Collections</h2>
          {loading ? (
            <p className={styles.loading}>Loading…</p>
          ) : collections.length === 0 ? (
            <p className={styles.loading}>No collections yet.</p>
          ) : (
            renderList(collections, "collections", "collection")
          )}
          {canManage && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (await create("collections", collectionName)) {
                  setCollectionName("");
                }
              }}
            >
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="col-name">New collection</label>
                  <input
                    id="col-name"
                    className={styles.input}
                    required
                    placeholder="Summer '27 Capsule"
                    value={collectionName}
                    onChange={(e) => setCollectionName(e.target.value)}
                  />
                </div>
              </div>
              <button className={styles.submit} type="submit" disabled={saving}>
                Add collection
              </button>
            </form>
          )}
        </section>
      </div>
    </div>
  );
};

export default Categories;
